import {
  ArrowUpRight,
  Globe2,
  Mail,
  MapPin,
  Phone
} from "lucide-react";
import { FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa";
import { Link } from "react-router-dom";
import { BRAND, CONTACT_INFO } from "../../constants/site";
import logoLight from "../../assets/logo_light.png";
import { footerData } from "../../data/homeData";
import CountryFlagIcon from "../common/CountryFlagIcon";

const socialIcons = {
  instagram: FaInstagram,
  linkedin: FaLinkedinIn,
  facebook: FaFacebookF
};

function Footer() {
  return (
    <footer className="relative bg-midnight-navy text-sky-glow border-t border-royal-blue/20">
      <div className="absolute inset-0 bg-gradient-to-b from-royal-blue/5 to-transparent pointer-events-none" />
      
      <div className="relative mx-auto grid w-full max-w-7xl gap-12 px-6 py-20 lg:grid-cols-[1.5fr_1fr_1fr_1.3fr]">
        <div>
          <Link to="/" className="group inline-block">
            <img
              src={logoLight}
              alt={BRAND.name}
              className="h-11 md:h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.02]"
            />
          </Link>
          <p className="mt-5 max-w-sm text-base leading-relaxed text-sky-glow/85">{BRAND.summary}</p>
        </div>

        <div>
          <h3 className="font-sans text-sm font-bold uppercase tracking-widest text-sky-glow/80">
            Quick Links
          </h3>
          <ul className="mt-6 space-y-4 text-sm font-medium">
            {footerData.quickLinks.map((link) => (
              <li key={link.to}>
                <Link className="inline-flex items-center gap-1 text-sky-glow/90 transition-colors hover:text-white" to={link.to}>
                  {link.label}
                  <ArrowUpRight className="h-3.5 w-3.5 opacity-50" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-sans text-sm font-bold uppercase tracking-widest text-sky-glow/80">
            Destinations
          </h3>
          <ul className="mt-6 space-y-4 text-sm font-medium">
            {footerData.destinationLinks.map((country) => (
              <li key={country.slug}>
                <Link
                  className="inline-flex items-center gap-2.5 text-sky-glow/90 transition-colors hover:text-white"
                  to={`/study-in/${country.slug}`}
                >
                  <CountryFlagIcon slug={country.slug} name={country.name} width={18} height={12} />
                  {country.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-sans text-sm font-bold uppercase tracking-widest text-sky-glow/80">
            Contact
          </h3>
          <ul className="mt-6 space-y-4 text-sm font-medium text-sky-glow/90">
            <li className="flex items-start gap-3">
              <Mail className="mt-0.5 h-4 w-4 text-warm-gold shrink-0" aria-hidden="true" />
              <a className="transition-colors hover:text-white" href={`mailto:${CONTACT_INFO.email}`}>
                {CONTACT_INFO.email}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Phone className="mt-0.5 h-4 w-4 text-warm-gold shrink-0" aria-hidden="true" />
              <a className="transition-colors hover:text-white" href={`tel:${CONTACT_INFO.phone.replace(/\s+/g, "")}`}>
                {CONTACT_INFO.phone}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-4 w-4 text-warm-gold shrink-0" aria-hidden="true" />
              <div>
                <p className="leading-relaxed">{CONTACT_INFO.address}</p>
                <a
                  className="mt-2 inline-flex text-xs font-bold uppercase tracking-wider text-royal-blue transition-colors hover:text-white"
                  href={CONTACT_INFO.mapUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  View on Map
                </a>
              </div>
            </li>
          </ul>

          <div className="mt-8 flex gap-3">
            {footerData.socialLinks.map((social) => {
              const iconKey = social.label.toLowerCase().replace(/\s+/g, "");
              const Icon = socialIcons[iconKey] || Globe2;

              return (
                <a
                  key={social.label}
                  href={social.to}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={social.label}
                  data-flight-message={`Follow us on ${social.label}`}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-sky-glow transition-all hover:scale-105 hover:border-warm-gold/50 hover:bg-warm-gold/10 hover:text-warm-gold"
                >
                  <Icon className="h-4 w-4" />
                </a>
              );
            })}
          </div>
        </div>
      </div>

      <div className="relative border-t border-white/10 py-6 text-center text-xs font-medium text-sky-glow/60">
        © {new Date().getFullYear()} NextEra Abroad. All rights reserved.
      </div>
    </footer>
  );
}

export default Footer;
